import { useSession } from '@clerk/clerk-react';
import * as EventTracker from '@src/analytics/EventTracker';
import * as TtfeTracker from '@src/analytics/TtfeTracker';
import { ORIGINAL_VIDEO_LANGUAGE_OPTIONS } from '@src/config/languages';
import { NEW_STORIES_COMPLETION_QUERY } from '@src/gql/story-gql';
import { useStoryUploadAnalytics } from '@src/monitoring/hooks/useStoryUploadAnalytics';
import { gpuWarmupService } from '@src/services/GpuWarmupService';
import {
  exceedsAudioEnhancementLimit,
  gbToBytes,
  STORY_VIDEO_SIZE_GB,
  validateStoryVideo,
} from '@src/services/upload/mediaValidation';
import { decideProcessingStart } from '@src/services/upload/sidecar';
import { classifyUploadError } from '@src/services/upload/UploadErrorClassifier';
import { uploadQueueService } from '@src/services/upload/UploadQueueService';
import { useUserStoryUploadFlow } from '@src/stores-v2/StoreContext';
import { isAudioSidecarEnabled } from '@src/utility/audioSidecarFlag';
import { LogManager } from '@src/utility/Logger';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { getStoryNameFromFileName } from '../utils';
import { useAudioSidecar } from './useAudioSidecar';

import type { SimpleSchemaTypes } from '@datocms/cma-client-browser';
import type { StoryCreationService } from '@src/services/StoryCreationService';
import type { ClassifiedUploadError } from '@src/services/upload/UploadErrorClassifier';
import type { ZoomRecordingFile } from '@src/services/ZoomService';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';
import type { UserIdentityStore } from '@src/stores-v2/UserIdentityStore';
import type { DatoStory, Showcase, Story } from '@src/types.ts/story';

const logger = LogManager.getLogger('hook:useStoryCreator');

export enum Step {
  one = 1,
  two,
  three,
  four,
  five,
}

const MAX_AWS_UPLOAD_SIZE_GB_NO_LIMIT = STORY_VIDEO_SIZE_GB.noLimit;
const MAX_AWS_UPLOAD_SIZE_GB_LIMIT = STORY_VIDEO_SIZE_GB.large;
const MAX_AWS_UPLOAD_SIZE_GB = STORY_VIDEO_SIZE_GB.default;
const MAX_STORY_TITLE_LENGTH = 120;

export type ArtifactUpload = SimpleSchemaTypes.Upload & { _internalId: string };
export type ArtifactFile = File & { _internalId: string };

export type StoryVideoMusicStrategy = 'keep' | 'process';

export interface ZoomRecordingData {
  recordingFiles: ZoomRecordingFile[];
  personId?: string;
  email?: string;
  password?: string;
}

type HookReturnType = {
  step: Step;
  goToNextStep: () => void;
  canGoToStep: (s: number) => boolean;
  handleStoryVideoUpload: (f: File[]) => void;
  handleStoryUrlUpload: (url: string) => void;
  handleStoryBRollUpload: (f: File[]) => void;
  retryUpload: () => Promise<void>;
  /** Resolved low-level upload mechanism for primary story video. */
  uploadMechanism: 'worker' | 'main-thread' | 'unknown';
  storyName: string;
  setStoryName: (s: string) => void;
  maxStoryTitleLength: number;
  storyLanguage: string | undefined;
  setStoryLanguage: (s: string) => void;
  storyType: 'raw' | 'produced' | null;
  setStoryType: (t: 'raw' | 'produced') => void;
  videoMusicStrategy: StoryVideoMusicStrategy;
  setVideoMusicStrategy: (s: StoryVideoMusicStrategy) => void;
  /** True when the selected file(s) are too large for audio enhancement (PLAT-11103). */
  audioEnhancementDisabled: boolean;
  storyArtifactsFiles: ArtifactFile[];
  storyArtifactsVideoFiles: ArtifactFile[];
  storyArtifacts: ArtifactUpload[];
  storyArtifactsVideo: ArtifactUpload[];
  saveArtifactNotes: (internalId: string) => void;
  setArtifactNotes: (internalId: string, notes: string) => void;
  deleteArtifact: (internalId: string) => void;
  saveArtifactVideoNotes: (internalId: string) => void;
  setArtifactVideoNotes: (internalId: string, notes: string) => void;
  deleteArtifactVideo: (internalId: string) => void;
  maxUploadSizeGB: number;
  setZoomRecordingData: (data: ZoomRecordingData | null) => void;
};

type Params = {
  organization?: Showcase;
  videoCreator: VideoCreatorStore | IVideoCreatorStore;
  datoClientStore: DatoClientStore;
  userIdentity: UserIdentityStore;
  datoPersonId?: string; // DatoCMS Person ID for the story teller
  modalId: string;
  onArtifactUploadError?: (kind: 'doc' | 'video', error: unknown) => void;
  storyCreationService: StoryCreationService;
  onStoryBlankCreated: (storyId: string) => void;
  onStoryInitialized: (s: Story) => void;
  onStoryCreated: (s: Story) => void;
  onStoryProgress: (percent: number, storyId?: string) => void;
  onError: (error: unknown, classified?: ClassifiedUploadError) => void;
};

export function useStoryCreator(params: Params): HookReturnType {
  const userStoryUploadFlowStore = useUserStoryUploadFlow();
  const { videoCreator } = params;
  const { datoClientStore } = params;
  const { currentRole } = params.userIdentity;
  const {
    enableLargeFileSizeUpload,
    enableNoLimitFileSizeUpload,
    enableStoryVideoSilenceCheck,
    transcriptionProviders,
  } = useFlagsCombination(currentRole);
  const maxUploadSizeGB = enableNoLimitFileSizeUpload
    ? MAX_AWS_UPLOAD_SIZE_GB_NO_LIMIT
    : enableLargeFileSizeUpload
      ? MAX_AWS_UPLOAD_SIZE_GB_LIMIT
      : MAX_AWS_UPLOAD_SIZE_GB;
  const storyTracker = useStoryUploadAnalytics();
  const storyCreationService = params.storyCreationService;
  const { session: clerkSession } = useSession();

  const [step, setStep] = useState<Step>(1);
  const [story, setStory] = useState<Story>();

  // step 1 data
  const [storyVideoFile, setStoryVideoFile] = useState<File | null>(null);
  const [storyVideoUrl, setStoryVideoUrl] = useState<string | null>(null);
  const blankStoryRef = useRef<DatoStory | null>(null);
  const storyVideoUploadCompleted = useRef<boolean>(false);
  // Set synchronously on the first file pick so a repeated trigger during the
  // async validation can't start a second pipeline (PLAT-11547).
  const videoUploadTriggerRef = useRef<{ at: number; stack?: string } | null>(
    null,
  );
  const lastTimeStoryLogUpdatedRef = useRef<number>(0);
  const lastUploadPercentLoggedRef = useRef<number>(0);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<boolean | null>(null);
  /**
   * Mirrors `uploadedFileUrl` for async callbacks that outlive the render they
   * closed over (the Sidecar-start fallback), where the state value is stale.
   */
  const originalLandedRef = useRef<boolean>(false);
  const markOriginalLanded = () => {
    originalLandedRef.current = true;
    setUploadedFileUrl(true);
  };
  const uploadMechanismRef = useRef<'worker' | 'main-thread' | 'unknown'>(
    'unknown',
  );
  // PLAT-11697 Audio Sidecar: demuxed at file-select, uploaded ahead of the
  // video, processing starts on whichever lands first.
  const sidecar = useAudioSidecar({
    modalId: params.modalId,
    storyCreationService: params.storyCreationService,
    getStoryId: () => blankStoryRef.current?.id,
    getOrganizationId: () => videoCreator.organization?.id,
  });
  /** Guards the one-shot POST /api/story/upload-task (Sidecar or Original). */
  const processingStartedRef = useRef<boolean>(false);
  const [multiFileVideoUrls, setMultiFileVideoUrls] = useState<string[] | null>(
    null,
  );
  const storedFilesRef = useRef<File[]>([]);
  const [isMultiFileUpload, setIsMultiFileUpload] = useState<boolean>(false);
  const [isUrlUpload, setIsUrlUpload] = useState<boolean>(false);
  const zoomRecordingDataRef = useRef<ZoomRecordingData | null>(null);
  const urlUploadIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  // step 2 data
  const [storyType, setStoryType] = useState<'raw' | 'produced' | null>(null);
  const [videoMusicStrategy, setVideoMusicStrategy] =
    useState<StoryVideoMusicStrategy>('process');
  const [audioEnhancementDisabled, setAudioEnhancementDisabled] =
    useState<boolean>(false);

  // step 3 data
  const [storyName, setStoryName] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [storyTellerName, _setStoryTellerName] = useState<string>(
    params.organization?.title || 'no_org',
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [storyTellerTitle, _setStoryTellerTitle] = useState<string>(
    'Organization storyteller',
  );
  const [storyLanguage, setStoryLanguage] = useState<string | undefined>(() => {
    const defaultLanguage =
      videoCreator?.organization?.defaultLanguage || 'en-us';
    return videoCreator?.organization?.allowLangSelectAtUserStoryUpload
      ? (
          ORIGINAL_VIDEO_LANGUAGE_OPTIONS.find(
            o => o.value === defaultLanguage,
          ) || ORIGINAL_VIDEO_LANGUAGE_OPTIONS[0]
        ).value
      : undefined;
  });

  // step 4 data
  const [storyArtifactsFiles, setStoryArtifactsFiles] = useState<
    ArtifactFile[]
  >([]);
  const [storyArtifacts, setStoryArtifacts] = useState<ArtifactUpload[]>([]);
  const [storyArtifactsVideoFiles, setStoryArtifactsVideoFiles] = useState<
    ArtifactFile[]
  >([]);
  const [storyArtifactsVideo, setStoryArtifactsVideo] = useState<
    ArtifactUpload[]
  >([]);
  const [bRollReady, setBRollReady] = useState<boolean>(false);
  const [storyUploadProcessingStarted, setStoryUploadProcessingStarted] =
    useState<boolean>(false);
  const storyArtifactsRef = useRef<ArtifactUpload[]>([]);
  const storyArtifactsFilesRef = useRef<ArtifactFile[]>([]);
  const storyArtifactsVideoRef = useRef<ArtifactUpload[]>([]);
  const storyArtifactsVideoFilesRef = useRef<ArtifactFile[]>([]);
  const lastProgressPercentRef = useRef<number>(0);
  const [nextCallback, setNextCallback] = useState<'init' | 'create'>('init');

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (urlUploadIntervalRef.current) {
        clearInterval(urlUploadIntervalRef.current);
        urlUploadIntervalRef.current = null;
      }
      // Cancel analytics lifecycle if initialized but not finalized
      // Success is triggered later after b-roll + processing completion; if user aborts early we cancel.
      if (!storyUploadProcessingStarted && storyTracker.transactionId) {
        storyTracker.trackCancel('unmount_before_processing');
      }
    };
  }, []);

  // Define all functions before useEffects to avoid linting errors
  const getStoryUpdates = async (): Promise<Partial<Story>> => {
    if (!story?.id) return {};
    try {
      const queryResult: {
        allStories: Partial<Story>[];
      } = await datoClientStore.gqlClient.request(
        NEW_STORIES_COMPLETION_QUERY,
        {
          ids: [story.id],
        },
      );
      return queryResult.allStories[0];
    } catch (err) {
      logger.warn('story-updates-fetch-failed', { error: err });
      return {};
    }
  };

  const callStoryProgressCbOnceFor = (percent: number, storyId?: string) => {
    if (percent > lastProgressPercentRef.current || percent === 100) {
      lastProgressPercentRef.current = percent;
      params.onStoryProgress(percent, storyId);
    }
  };

  const handleVideoUploadProgress = async (
    progress: number,
    done?: boolean,
  ) => {
    sidecar.onVideoProgress(progress, done);
    // If blank story not yet created, queue the highest observed progress.
    if (!blankStoryRef.current) {
      return; // Defer logging & callbacks until story exists
    }
    const timestamp = Date.now();
    if (
      !storyVideoUploadCompleted.current &&
      (timestamp - lastTimeStoryLogUpdatedRef.current > 10000 ||
        progress - lastUploadPercentLoggedRef.current > 10 ||
        done)
    ) {
      try {
        lastTimeStoryLogUpdatedRef.current = timestamp;
        lastUploadPercentLoggedRef.current = progress;
        await datoClientStore.storyRepository.updateStoryStepsLog({
          id: blankStoryRef.current.uploadLog,
          initial_upload: JSON.stringify({
            ...(done ? { status: 'success' } : {}),
            progress,
            updatedAt: timestamp,
          }),
        });
      } catch (err) {
        logger.warn('upload-log-update-failed', { error: err });
        lastTimeStoryLogUpdatedRef.current = timestamp - 10000;
        lastUploadPercentLoggedRef.current = progress - 10;
      }
    }
    const progressKey = blankStoryRef.current?.id ?? params.modalId;
    userStoryUploadFlowStore.updateUploadingProgress(progressKey, progress);
    callStoryProgressCbOnceFor(
      Math.round(progress / 2),
      blankStoryRef.current?.id,
    );
  };

  const formatCurrentUserData = () => {
    const userData = params.userIdentity.getUserIdentity();
    if (!userData) {
      return null;
    }
    const { id, name, email, type } = userData;
    return { id, name, email: email ?? '', type };
  };

  const handleStoryCreate = async () => {
    if (!blankStoryRef.current) {
      setTimeout(() => {
        handleStoryCreate();
      }, 5 * 1000);
      return;
    }

    try {
      if (!storyType) {
        logger.warn('story-type-not-selected');
        return;
      }
      const storyUpdate = await videoCreator.updateStory({
        id: blankStoryRef.current.id,
        title: storyName,
        storyTeller: blankStoryRef.current.storyTeller,
        storyType,
        originalVideoLanguage: storyLanguage,
      });
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      setStory({
        ...blankStoryRef.current,
        ...storyUpdate,
        // The CMA update echoes `story_teller` as a bare record ID, so the
        // camelCased update would replace the `{ id, name }` object with a
        // string and leave `storyTeller.name` undefined for every consumer of
        // this story (it is handed straight to the Story Manager list).
        storyTeller: blankStoryRef.current.storyTeller,
      } as Story); // transitional: refine types in future slice
    } catch (error) {
      params.onError(error);
    }
  };

  const createBlankStory = async (
    initialTitle?: string,
  ): Promise<DatoStory> => {
    return storyCreationService.createBlankStory({
      storyType,
      storyTellerName,
      storyTellerTitle,
      // The wizard's Save applies the user's final name later; this only
      // replaces the interim uuid placeholder in the Story Manager list.
      initialTitle: initialTitle || storyName || undefined,
      organization: params.organization,
      userData: formatCurrentUserData(),
    });
  };

  const startProcessingUploadedVideo = async (audioSidecarKey?: string) => {
    if (!story) {
      logger.warn('no-story-for-processing');
      return;
    }
    if (!storyType) {
      logger.warn('no-story-type-for-processing');
      return;
    }
    try {
      const organizationId = params.organization?.id;
      await storyCreationService.startProcessingUploadedVideo({
        storyId: story.id,
        storyName,
        storyType,
        storyVideoUrl,
        isUrlUpload,
        isMultiFileUpload,
        multiFileVideoUrls,
        storyVideoFile: storyVideoFile,
        organizationId,
        videoMusicStrategy,
        audioSidecarKey,
        tracker: storyTracker, // transitional
        transcriptionProviders,
        zoomRecordingFiles: zoomRecordingDataRef.current?.recordingFiles,
        zoomPersonId: zoomRecordingDataRef.current?.personId,
        zoomEmail: zoomRecordingDataRef.current?.email,
        zoomPassword: zoomRecordingDataRef.current?.password,
      });
      zoomRecordingDataRef.current = null;
      setIsMultiFileUpload(false);
      setMultiFileVideoUrls(null);
      setIsUrlUpload(false);
      setStoryVideoUrl(null);
    } catch (error) {
      if (audioSidecarKey) {
        // Accelerator rule (PLAT-11697): a failed Sidecar start must never fail
        // the upload. Forget the Sidecar and start on the Original instead —
        // now if it has already landed, otherwise when the effect sees it land.
        logger.warn('sidecar-start-failed-falling-back', {
          modalId: params.modalId,
          storyId: story.id,
          error,
        });
        sidecar.forgetKey();
        processingStartedRef.current = false;
        if (originalLandedRef.current) {
          processingStartedRef.current = true;
          await startProcessingUploadedVideo();
        }
        return;
      }
      logger.error('start-processing-failed', {
        modalId: params.modalId,
        storyId: story?.id,
        error,
      });
      params.onError(error);
    }
  };

  // Start processing on whichever lands first: the Audio Sidecar (PLAT-11697,
  // with `audioSidecarKey`) or the Original (today's call). One POST per
  // story; the server is idempotent but we do not rely on it. The visible
  // phase change (progress → processing) still follows the Original.
  useEffect(() => {
    if (!story || !videoMusicStrategy) return;
    const start = decideProcessingStart({
      processingStarted: processingStartedRef.current,
      storyReady: true,
      sidecarLanded: sidecar.landed,
      audioSidecarKey: sidecar.getKey(),
      originalLanded: !!uploadedFileUrl,
    });
    if (start) {
      processingStartedRef.current = true;
      logger.info('processing-triggered', {
        modalId: params.modalId,
        storyId: story.id,
        videoMusicStrategy,
        audioPath: start.source === 'sidecar' ? 'sidecar' : 'server-extract',
      });
      (async () => {
        await startProcessingUploadedVideo(
          start.source === 'sidecar' ? start.audioSidecarKey : undefined,
        );
      })();
    }
    if (uploadedFileUrl && !storyVideoUploadCompleted.current) {
      storyVideoUploadCompleted.current = true;
      setStoryUploadProcessingStarted(true);
      const processingKey = blankStoryRef.current?.id ?? params.modalId;
      userStoryUploadFlowStore.startProcessing(processingKey);
    }
  }, [uploadedFileUrl, sidecar.landed, story, storyType, videoMusicStrategy]);

  useEffect(() => {
    // Create story when reaching step 5 (after user has edited name in step 3)
    if (step === Step.five && !story) {
      handleStoryCreate();
    }
    if (step === Step.five && story?.id && nextCallback === 'init') {
      // lastProgressPercentRef.current = Infinity; // stop progress updates after story is initialized
      (async () => {
        params.onStoryInitialized({
          ...story,
          ...(await getStoryUpdates()),
        });
        setNextCallback('create');
      })();
    }
  }, [step, story?.id, nextCallback]);

  useEffect(() => {
    logger.debug('step-change', {
      step,
      started: storyUploadProcessingStarted,
      callback: nextCallback,
    });
    if (
      step === Step.five &&
      story &&
      storyUploadProcessingStarted &&
      bRollReady &&
      nextCallback === 'create'
    ) {
      (async () => {
        const created = { ...story, ...(await getStoryUpdates()) };
        params.onStoryCreated(created);
        userStoryUploadFlowStore.markComplete(created.id);
      })();
    }
  }, [step, story, storyUploadProcessingStarted, bRollReady, nextCallback]);

  useEffect(() => {
    logger.debug('artifacts-changed', { count: storyArtifacts.length });
    // to prevent methods' closure from capturing stale state
    storyArtifactsRef.current = storyArtifacts;
    storyArtifactsFilesRef.current = storyArtifactsFiles;
    storyArtifactsVideoRef.current = storyArtifactsVideo;
    storyArtifactsVideoFilesRef.current = storyArtifactsVideoFiles;

    // b-roll is uploaded when story artifacts' length matches files' length
    // Since step 4 is removed, artifacts arrays will be empty (no b-roll uploaded)
    if (
      storyArtifacts.length === storyArtifactsFiles.length &&
      storyArtifactsVideo.length === storyArtifactsVideoFiles.length
    ) {
      // if we are at the last step, time to update story with uploaded b-roll and mark b-roll as ready
      // doing it at the last step makes it less error prone, because b-roll can change multiple times at step 4
      if (story && step === Step.five) {
        (async () => {
          if (
            storyArtifactsFiles.length > 0 ||
            storyArtifactsVideoFiles.length > 0
          ) {
            try {
              const storyUpdate = await videoCreator.updateStory({
                id: story.id,
                storyArtifacts: storyArtifacts.map(a => ({
                  id: a.id,
                })),
                storyArtifactsVideo: storyArtifactsVideo.map(a => ({
                  id: a.id,
                })),
              });
              setStory({
                ...story,
                ...storyUpdate,
                // The CMA echo camelCases `story_teller` back as a bare record
                // ID — keep the resolved object (same as handleStoryCreate).
                storyTeller: story.storyTeller,
              });
            } catch (error) {
              logger.warn('artifact-update-failed', { error });
            }
          }
          // Mark b-roll as ready even if no artifacts (step 4 was skipped)
          setBRollReady(true);
        })();
      }
      // initial b-roll upload is done - time to update progress, prematurely
      // TODO TEMP disabled, needs to be revisited
      // if (
      //   storyArtifactsFiles.length > 0 ||
      //   storyArtifactsVideoFiles.length > 0 ||
      //   step === Step.five
      // ) {
      //   callStoryProgressCbOnceFor(50);
      // }
    }
  }, [
    storyArtifacts,
    storyArtifactsFiles,
    storyArtifactsVideo,
    storyArtifactsVideoFiles,
    step,
    story?.id,
  ]);

  const goToNextStep = () => {
    // Skip step 4 (b-roll upload) - go directly from step 3 to step 5
    if (step === Step.three) {
      setStep(Step.five);
    } else {
      setStep(step + 1);
    }
  };

  /**
   * Execute the upload portion (create blank story if needed, then upload files).
   * Separated from handleStoryVideoUpload so retry can re-invoke without re-running
   * wizard setup (name, step advance, analytics init).
   */
  const performUpload = async (files: File[]) => {
    const uploadStartTime = performance.now();
    const isMultipleFiles = files.length > 1;
    const file = files[0];
    const totalSize = isMultipleFiles
      ? files.reduce((sum, f) => sum + f.size, 0)
      : file.size;

    // Fire-and-forget: pre-warm GPU instances while upload proceeds
    const warmupToken = await clerkSession?.getToken({
      template: 'aws_federation',
    });
    gpuWarmupService.warmup(warmupToken || undefined);

    logger.info('perform-upload-start', {
      modalId: params.modalId,
      storyId: blankStoryRef.current?.id,
      fileCount: files.length,
      fileName: file.name,
      totalSizeBytes: totalSize,
      isRetry: !!blankStoryRef.current,
    });

    try {
      // Only create blank story if one doesn't already exist (first attempt vs retry)
      if (!blankStoryRef.current) {
        // Title from the file directly — `storyName` state set in the same
        // event as the file pick isn't committed yet when this runs.
        blankStoryRef.current = await createBlankStory(
          getStoryNameFromFileName(file.name, MAX_STORY_TITLE_LENGTH),
        );
        logger.info('blank-story-created', {
          modalId: params.modalId,
          storyId: blankStoryRef.current.id,
        });
        params.onStoryBlankCreated(blankStoryRef.current.id);
        TtfeTracker.storyCreated(params.modalId, blankStoryRef.current.id);
      }

      // PLAT-11228 (audio-first): keep the local File so player surfaces can
      // play it while renditions are pending. Single-file uploads only.
      if (!isMultipleFiles) {
        userStoryUploadFlowStore.registerLocalVideo(
          blankStoryRef.current.id,
          file,
        );
      }

      // Wait for upload slot (may be immediate or queued behind another upload)
      const progressKey = blankStoryRef.current?.id ?? params.modalId;
      await uploadQueueService.acquireSlot(progressKey);
      logger.info('queue-slot-acquired', {
        modalId: params.modalId,
        storyId: blankStoryRef.current?.id,
        progressKey,
      });
      userStoryUploadFlowStore.activateUpload(progressKey);

      // Fetch Clerk token AFTER acquiring the slot so it doesn't expire while queued
      const clerkToken = await clerkSession?.getToken({ skipCache: true });
      if (!isMultipleFiles) {
        // Validation passed, blank story exists, credentials reachable: the
        // Sidecar may be uploaded as soon as (or if) the demux is done.
        sidecar.markUploadReady(clerkToken || undefined);
      }

      try {
        if (isMultipleFiles) {
          setIsMultiFileUpload(true);
          const result = await storyCreationService.uploadMultiFiles(
            files,
            blankStoryRef.current.id,
            videoCreator.organization?.id,
            clerkToken || undefined,
            {
              onProgress: (p: number, done?: boolean) =>
                handleVideoUploadProgress(p, done),
              onUploaded: markOriginalLanded,
              onError: (err: unknown) => {
                const classified = classifyUploadError(err);
                params.onError(err, classified);
              },
            },
            mechanism => {
              uploadMechanismRef.current = mechanism;
            },
          );
          setMultiFileVideoUrls(result.uploadedUrls);
        } else {
          await storyCreationService.uploadSingleFile(
            file,
            blankStoryRef.current.id,
            videoCreator.organization?.id,
            clerkToken || undefined,
            {
              onProgress: (p: number, done?: boolean) =>
                handleVideoUploadProgress(p, done),
              onUploaded: () => {
                storyTracker.trackSuccess({
                  fileSizeBytes: file.size,
                });
                sidecar.onVideoLanded();
                return markOriginalLanded();
              },
              onError: (err: unknown) => {
                const classified = classifyUploadError(err);
                params.onError(err, classified);
              },
            },
            mechanism => {
              uploadMechanismRef.current = mechanism;
            },
          );
        }

        storyTracker.trackSuccess({
          fileSizeBytes: file.size,
        });
        const uploadWallTimeMs = Math.round(
          performance.now() - uploadStartTime,
        );
        EventTracker.trackStoryUploadCompleted({
          story_id: blankStoryRef.current?.id,
          file_size_mb: Math.round((totalSize / (1024 * 1024)) * 100) / 100,
          duration_seconds: Math.round(uploadWallTimeMs / 1000),
          file_size_bytes: totalSize,
          upload_wall_time_ms: uploadWallTimeMs,
          upload_throughput_bytes_per_sec:
            uploadWallTimeMs > 0
              ? Math.round(totalSize / (uploadWallTimeMs / 1000))
              : undefined,
          ...TtfeTracker.uploadCompletedProps(
            blankStoryRef.current?.id ?? params.modalId,
          ),
        });
        handleVideoUploadProgress(100);
        markOriginalLanded();
        logger.info('upload-completed', {
          modalId: params.modalId,
          storyId: blankStoryRef.current?.id,
          mechanism: uploadMechanismRef.current,
        });
      } finally {
        uploadQueueService.releaseSlot();
      }
    } catch (error) {
      const classified = classifyUploadError(error);
      userStoryUploadFlowStore.markError(
        classified.userMessage,
        blankStoryRef.current?.id,
        classified.kind,
        classified.retryable,
        params.modalId,
      );
      storyTracker.trackFailure(error, { errorKind: classified.kind });
      storyTracker.analyticsLog('error', 'upload-failed', {
        reason: classified.technicalDetail,
      });
      EventTracker.trackStoryUploadFailed({
        story_id: blankStoryRef.current?.id,
        file_size_mb: Math.round((totalSize / (1024 * 1024)) * 100) / 100,
        error_message: classified.technicalDetail,
      });
      params.onError(error, classified);
    }
  };

  /**
   * Handle initial user-selected video file(s).
   * Side-effects:
   *  - Creates blank story (async)
   *  - Initiates upload(s) (single or multi-concat)
   *  - Advances wizard to Step.two immediately (progress updates async)
   *  - Records analytics init event
   */
  const handleStoryVideoUpload = async (files: File[]) => {
    if (!files.length) {
      logger.warn('no-files-at-upload');
      return;
    }

    if (videoUploadTriggerRef.current) {
      // Root cause unknown — keep both stacks to find the second caller.
      logger.warn('video-upload-repeated-trigger', {
        modalId: params.modalId,
        fileCount: files.length,
        fileName: files[0].name,
        msSinceFirstTrigger: Math.round(
          performance.now() - videoUploadTriggerRef.current.at,
        ),
        firstStack: videoUploadTriggerRef.current.stack,
        stack: new Error().stack,
      });
      return;
    }
    videoUploadTriggerRef.current = {
      at: performance.now(),
      stack: new Error().stack,
    };

    // PLAT-11697: start the Audio Sidecar demux at file-select, in parallel
    // with validation and the video upload; it is only uploaded after
    // validation has passed and the blank story exists. Single local files only.
    if (files.length === 1 && isAudioSidecarEnabled()) {
      sidecar.start(files[0]);
    }

    // Validate every file up front (PLAT-10885). Invalid files are rejected
    // here so they never create a story, reach S3, or trigger the expensive
    // backend pipeline (MediaConvert + transcription). Fail-closed: an
    // unreadable file is blocked, not passed through.
    const maxSizeBytes = gbToBytes(maxUploadSizeGB);
    const validations = await Promise.all(
      files.map(f =>
        validateStoryVideo(f, {
          maxSizeBytes,
          requireAudio: enableStoryVideoSilenceCheck,
        }),
      ),
    );
    for (let i = 0; i < validations.length; i += 1) {
      const result = validations[i];
      if (result.ok) continue;
      const prefix = files.length > 1 ? `${files[i].name}: ` : '';
      videoCreator.showToast({
        state: 'error',
        message: `${prefix}${result.error.message}`,
      });
      logger.warn('story-video-validation-rejected', {
        modalId: params.modalId,
        reason: result.error.reason,
        detail: result.error.detail,
        fileName: files[i].name,
        fileSize: files[i].size,
        fileCount: files.length,
      });
      EventTracker.trackStoryVideoRejected({
        reason: result.error.reason,
        file_size_mb: Math.round((files[i].size / (1024 * 1024)) * 100) / 100,
        file_type: files[i].type,
      });
      sidecar.abort();
      // Rejected files never start a pipeline — let the user pick another.
      videoUploadTriggerRef.current = null;
      return;
    }

    const isMultipleFiles = files.length > 1;
    const file = files[0];
    const totalSize = isMultipleFiles
      ? files.reduce((sum, f) => sum + f.size, 0)
      : file.size;

    // Files too large for the ffmpeg mux Lambda can't go through audio
    // enhancement — disable the option and flip the default off (PLAT-11103).
    const enhancementDisabled = exceedsAudioEnhancementLimit(totalSize);
    setAudioEnhancementDisabled(enhancementDisabled);
    if (enhancementDisabled) {
      setVideoMusicStrategy('keep');
      logger.info('audio-enhancement-disabled-for-size', {
        modalId: params.modalId,
        totalSizeBytes: totalSize,
        fileCount: files.length,
      });
    }

    logger.info('video-upload-initiated', {
      modalId: params.modalId,
      fileCount: files.length,
      fileName: file.name,
      totalSizeBytes: totalSize,
    });

    setStoryVideoFile(file);
    storedFilesRef.current = files;

    setStoryName(getStoryNameFromFileName(file.name, MAX_STORY_TITLE_LENGTH));
    handleVideoUploadProgress(1);
    goToNextStep();

    // Initialize upload phase with file metadata (uses modalId as temp key)
    userStoryUploadFlowStore.startUploading(
      file.name,
      totalSize,
      undefined,
      params.modalId,
    );

    storyTracker.trackInit({
      fileSizeBytes: totalSize,
      fileType: file.type,
      isConcat: isMultipleFiles,
      fileCount: files.length,
    });
    EventTracker.trackStoryUploadStarted({
      story_id: undefined,
      file_name: file.name,
      file_size_mb: Math.round((totalSize / (1024 * 1024)) * 100) / 100,
      file_type: file.type,
    });
    // TTFE clock start (PLAT-11223) — no storyId yet; the session is keyed by
    // modalId until the blank story is created in performUpload.
    TtfeTracker.fileSelected(params.modalId, {
      fileSizeBytes: totalSize,
      fileCount: files.length,
      fileType: file.type,
    });
    sidecar.flushTelemetry();

    await performUpload(files);
  };

  const retryUpload = async () => {
    if (!storyVideoFile) return;
    logger.info('retry-upload-initiated', {
      modalId: params.modalId,
      storyId: blankStoryRef.current?.id,
      fileCount: storedFilesRef.current.length || 1,
    });
    const retryKey = blankStoryRef.current?.id ?? params.modalId;
    userStoryUploadFlowStore.resetToUploading(retryKey);
    storyVideoUploadCompleted.current = false;
    originalLandedRef.current = false;
    setUploadedFileUrl(null);
    sidecar.resetVideoProgress();
    const files =
      storedFilesRef.current.length > 1
        ? storedFilesRef.current
        : [storyVideoFile];
    await performUpload(files);
  };

  const handleStoryUrlUpload = async (url: string) => {
    logger.info('url-upload-initiated', { url });

    setStoryVideoUrl(url);
    setIsUrlUpload(true);
    // URL uploads have no known size at selection time — out of scope for the
    // audio-enhancement size gate; clear any flag left by a prior file pick.
    setAudioEnhancementDisabled(false);
    handleVideoUploadProgress(1);
    goToNextStep();

    storyTracker.trackInit({
      fileType: 'video/mp4', // Assume MP4 for URLs
      isUrlUpload: true,
    });

    try {
      // Create blank story with initial metadata
      const initialMetadata = {
        uploadType: 'url',
        source: 'zoom',
        initiatedAt: new Date().toISOString(),
        status: 'initiated',
      };

      // Extract filename from URL for title if possible
      let urlTitle = `url-upload-${uuid()}`;
      try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        const filename = pathname.split('/').pop();
        if (filename && filename !== '') {
          urlTitle = filename.replace(/\.[^/.]+$/, ''); // Remove file extension
        }
      } catch (e) {
        // Keep default title if URL parsing fails
      }

      // Use datoPersonId if provided, otherwise fall back to upsertStoryTeller
      const storyTellerId =
        params.datoPersonId ||
        (
          await videoCreator.upsertStoryTeller({
            name: storyTellerName,
            title: storyTellerTitle,
          })
        ).id;

      const storyTemplate = await videoCreator.createStory({
        title: urlTitle,
        storyType: storyType || undefined,
        storyTeller: {
          name: storyTellerName,
          id: storyTellerId,
        },
        primaryShowcase: params.organization,
        externalUploadLog: initialMetadata,
        externalUploadStatus: 'IN PROGRESS',
        byExternalUser: true,
      });
      blankStoryRef.current = storyTemplate;
      params.onStoryBlankCreated(storyTemplate.id);

      // Set flags for URL upload
      setIsUrlUpload(true);

      // Start with initial progress
      handleVideoUploadProgress(5);

      // For URL uploads, immediately trigger the step function
      // The Lambda will handle the download asynchronously
      markOriginalLanded();

      // Clear any existing interval first
      if (urlUploadIntervalRef.current) {
        clearInterval(urlUploadIntervalRef.current);
      }

      // Poll DatoCMS for real progress updates
      urlUploadIntervalRef.current = setInterval(async () => {
        try {
          // Fetch the story to get the latest external_upload_log
          const updatedStory = await datoClientStore.storyRepository?.findOne(
            storyTemplate.id,
          );

          if (updatedStory?.externalUploadLog) {
            const uploadLog =
              typeof updatedStory.externalUploadLog === 'string'
                ? JSON.parse(updatedStory.externalUploadLog)
                : updatedStory.externalUploadLog;

            // Update progress based on real data from Lambda
            if (uploadLog.progress !== undefined) {
              handleVideoUploadProgress(uploadLog.progress);

              // If completed, clean up and mark as done
              if (
                uploadLog.fetchStatus === 'completed' ||
                uploadLog.progress >= 100
              ) {
                if (urlUploadIntervalRef.current) {
                  clearInterval(urlUploadIntervalRef.current);
                  urlUploadIntervalRef.current = null;
                }
                markOriginalLanded();
                handleVideoUploadProgress(100, true); // ensure final event
                storyTracker.trackSuccess();
              }
            }
          }
        } catch (error) {
          logger.error('url-upload-polling-failed', { error });
        }
      }, 2000); // Poll every 2 seconds

      // Cleanup polling after 5 minutes (just for cleanup, doesn't affect processing)
      setTimeout(() => {
        if (urlUploadIntervalRef.current) {
          clearInterval(urlUploadIntervalRef.current);
          urlUploadIntervalRef.current = null;
          // Fallback to mark as complete if polling fails
          if (lastProgressPercentRef.current < 100) {
            markOriginalLanded();
            handleVideoUploadProgress(100, true);
          }
        }
      }, 300000); // 5 minute timeout for polling cleanup only
    } catch (error) {
      params.onError(error);
      storyTracker.trackFailure(error);
    }
  };

  const handleStoryBRollUpload = async (files: File[]) => {
    logger.debug('broll-upload-initiated', { fileCount: files.length });
    const imageFiles = files.filter((f): f is ArtifactFile =>
      f.type.includes('image'),
    );
    imageFiles.forEach(f => {
      f._internalId = uuid(); // eslint-disable-line no-param-reassign
    });

    const videoFiles = files.filter((f): f is ArtifactFile =>
      f.type.includes('video'),
    );
    videoFiles.forEach(f => {
      f._internalId = uuid(); // eslint-disable-line no-param-reassign
    });

    let newStoryArtifactsFiles =
      storyArtifactsFilesRef.current.concat(imageFiles);
    setStoryArtifactsFiles(newStoryArtifactsFiles);
    let newStoryArtifactsVideoFiles =
      storyArtifactsVideoFilesRef.current.concat(videoFiles);
    setStoryArtifactsVideoFiles(newStoryArtifactsVideoFiles);

    let newStoryArtifacts = [...storyArtifactsRef.current];

    const imageUploadPromises = imageFiles.map(async file => {
      try {
        const upload =
          await datoClientStore.datoClient?.uploads.createFromFileOrBlob({
            fileOrBlob: file,
            filename: file.name,
          });
        if (upload) {
          return {
            ...upload,
            _internalId: file._internalId,
          };
        }
        return null;
      } catch (err) {
        params.onArtifactUploadError?.('doc', err);
        return { error: true, _internalId: file._internalId };
      }
    });

    const imageResults = await Promise.all(imageUploadPromises);

    imageResults.forEach(result => {
      if (!result) return; // skip nulls
      if ('error' in result) {
        // Remove failed upload placeholder from files list
        newStoryArtifactsFiles = newStoryArtifactsFiles.filter(
          f => f._internalId !== result._internalId,
        );
      } else {
        // Successful upload
        newStoryArtifacts = newStoryArtifacts.concat(result);
      }
    });

    setStoryArtifacts(newStoryArtifacts);
    setStoryArtifactsFiles(newStoryArtifactsFiles);
    logger.debug('broll-artifacts-set', { count: newStoryArtifacts.length });

    let newStoryArtifactsVideo = [...storyArtifactsVideoRef.current];

    const videoUploadPromises = videoFiles.map(async file => {
      try {
        const upload =
          await datoClientStore.datoClient?.uploads.createFromFileOrBlob({
            fileOrBlob: file,
            filename: file.name,
          });
        if (upload) {
          return {
            ...upload,
            _internalId: file._internalId,
          };
        }
        return null;
      } catch (err) {
        params.onArtifactUploadError?.('video', err);
        return { error: true, _internalId: file._internalId };
      }
    });

    const videoResults = await Promise.all(videoUploadPromises);

    videoResults.forEach(result => {
      if (!result) return;
      if ('error' in result) {
        newStoryArtifactsVideoFiles = newStoryArtifactsVideoFiles.filter(
          f => f._internalId !== result._internalId,
        );
      } else {
        newStoryArtifactsVideo = newStoryArtifactsVideo.concat(result);
      }
    });

    setStoryArtifactsVideo(newStoryArtifactsVideo);
    setStoryArtifactsVideoFiles(newStoryArtifactsVideoFiles);
  };

  const setZoomRecordingData = (data: ZoomRecordingData | null) => {
    zoomRecordingDataRef.current = data;
  };

  const canGoToStep = (nextStep: number): boolean => {
    if (nextStep === Step.two) {
      return !!storyVideoFile;
    }
    if (nextStep === Step.three) {
      return !!storyType;
    }
    if (nextStep === Step.four) {
      return (
        !!storyName &&
        !!(storyTellerName || '').trim() &&
        storyName.length <= MAX_STORY_TITLE_LENGTH
      );
    }
    if (nextStep === Step.five) {
      // Allow going to step 5 from step 3 (skipping step 4)
      return (
        !!storyName &&
        !!(storyTellerName || '').trim() &&
        storyName.length <= MAX_STORY_TITLE_LENGTH
      );
    }
    return true;
  };

  const saveArtifactNotes = async (internalId: string): Promise<void> => {
    const artifact = storyArtifactsRef.current.find(
      a => a._internalId === internalId,
    );
    await doSaveArtifactNotes(artifact);
  };

  const doSaveArtifactNotes = async (artifact?: ArtifactUpload) => {
    if (!artifact) {
      return;
    }
    try {
      await datoClientStore.assetRepository?.update(artifact.id, {
        title: artifact.notes || '',
        alt: artifact.notes || '',
      });
    } catch (err) {
      logger.warn('artifact-notes-save-failed', { error: err });
    }
  };

  const setArtifactNotes = (internalId: string, notes: string): void => {
    setStoryArtifacts(prev =>
      prev.map(a => (a._internalId === internalId ? { ...a, notes } : a)),
    );
  };

  const deleteArtifact = async (internalId: string): Promise<void> => {
    const artifact = storyArtifactsRef.current.find(
      a => a._internalId === internalId,
    );
    if (!artifact) {
      return;
    }

    setStoryArtifacts(
      storyArtifactsRef.current.filter(a => a._internalId !== internalId),
    );
    setStoryArtifactsFiles(
      storyArtifactsFilesRef.current.filter(f => f._internalId !== internalId),
    );

    try {
      await datoClientStore.assetRepository?.delete(artifact.id);
    } catch (err) {
      logger.warn('broll-delete-failed', { error: err });
    }
  };

  const saveArtifactVideoNotes = async (internalId: string): Promise<void> => {
    const artifact = storyArtifactsVideoRef.current.find(
      a => a._internalId === internalId,
    );
    await doSaveArtifactNotes(artifact);
  };

  const setArtifactVideoNotes = (internalId: string, notes: string): void => {
    setStoryArtifactsVideo(
      storyArtifactsVideoRef.current.map(a =>
        a._internalId === internalId ? { ...a, notes } : a,
      ),
    );
  };

  const deleteArtifactVideo = async (internalId: string): Promise<void> => {
    const artifact = storyArtifactsVideoRef.current.find(
      a => a._internalId === internalId,
    );
    if (!artifact) {
      return;
    }

    setStoryArtifactsVideo(
      storyArtifactsVideoRef.current.filter(a => a._internalId !== internalId),
    );
    setStoryArtifactsVideoFiles(
      storyArtifactsVideoFilesRef.current.filter(
        f => f._internalId !== internalId,
      ),
    );

    try {
      await datoClientStore.assetRepository?.delete(artifact.id);
    } catch (err) {
      logger.warn('broll-video-delete-failed', { error: err });
    }
  };

  return {
    step,
    goToNextStep,
    canGoToStep,
    handleStoryVideoUpload,
    handleStoryUrlUpload,
    handleStoryBRollUpload,
    retryUpload,
    uploadMechanism: uploadMechanismRef.current,
    storyName,
    setStoryName,
    storyLanguage,
    setStoryLanguage,
    storyType,
    setStoryType,
    videoMusicStrategy,
    setVideoMusicStrategy,
    audioEnhancementDisabled,
    storyArtifactsFiles,
    storyArtifactsVideoFiles,
    storyArtifacts,
    storyArtifactsVideo,
    saveArtifactNotes,
    setArtifactNotes,
    deleteArtifact,
    saveArtifactVideoNotes,
    setArtifactVideoNotes,
    deleteArtifactVideo,
    maxUploadSizeGB,
    maxStoryTitleLength: MAX_STORY_TITLE_LENGTH,
    setZoomRecordingData,
  };
}
